import type { AriaTextFieldProps } from '@react-types/textfield';
import type { RefObject } from 'react';
import React, { useRef } from 'react';
import { useTextField } from 'react-aria';
import { mergeRefs } from 'react-merge-refs';

import type { ControlSize } from '../../controls/shared/types';
import type { RenameKeys } from '../../types/rename_keys';
import type { InputSharedProps } from '../BaseInput/BaseInput';
import { useControlSize } from '../../controls/shared/context';
import { selectors } from '../../controls/shared/styles';
import { styled } from '../../stitches.config';
import { BaseInput } from '../BaseInput/BaseInput';

type RemappedAriaTextFieldProps = RenameKeys<AriaTextFieldProps, { isDisabled: 'disabled' }>;

export interface SearchInputProps extends InputSharedProps, RemappedAriaTextFieldProps {
  size?: ControlSize;
  inputRef?: RefObject<HTMLInputElement | null>;
  invalid?: boolean;
  preset?: 'none' | 'table';
  scope?: 'full' | 'scoped';
}

const SearchInputField = styled(BaseInput, {
  variants: {
    preset: {
      none: {},
      table: {
        '@notDesktop': {
          [`${selectors.focus}, &:has(input:not(:placeholder-shown))`]: {
            widthAll: '$220',
          },
        },

        '@desktop': {
          [`${selectors.focus}, &:has(input:not(:placeholder-shown))`]: {
            widthAll: '$180',
          },
        },
      },
    },
  },
});
SearchInputField.displayName = 'SearchInputField';

export function SearchInput({
  size,
  disabled = false,
  inputRef: inputRefProp,
  invalid,
  maxWidth,
  minWidth,
  prefix,
  preset = 'table',
  scope,
  suffix,
  width,
  ref,
  ...props
}: SearchInputProps & { ref?: React.Ref<HTMLLabelElement> }) {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ? mergeRefs([inputRefProp, internalInputRef]) : internalInputRef;
  const { inputProps } = useTextField({ ...props, isDisabled: disabled }, internalInputRef);
  const controlSize = useControlSize(size);

  return (
    <SearchInputField
      ref={ref}
      type="search"
      inputRef={inputRef}
      inputProps={inputProps}
      invalid={invalid}
      disabled={disabled}
      icon="search"
      preset={preset}
      prefix={prefix}
      suffix={suffix}
      size={controlSize}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    />
  );
}
